import {dataTestId} from '@hconnect/uikit'
import {customThemeConstants} from '@hconnect/uikit/src/lib2'
import {Close} from '@mui/icons-material'
import {IconButton, Skeleton, Stack, useTheme} from '@mui/material'
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {Legend, LegendItem} from '../../../../shared/components/Legend'
import {toPixel} from '../../../../shared/helpers/utils'
import {useHistoryStoragesWithMaterialQuery} from '../../../../shared/hooks/api'
import {usePlanningChartStartEnd} from '../../../page-planning/dailyPlanning/PlanningChartStartEndProvider'
import {SelectMaterialIdDropdown} from '../../../page-planning/dailyPlanning/stockLevels/detail/SelectMaterialIdDropdown'
import {useSelectedOptimizerPlan} from '../../SelectedOptimizerPlanProvider'
import {useSelectedMaterialId} from '../SelectedMaterialIdProvider'
import {useGetOptimizedMaterialIds} from '../utils/useGetOptimizedMaterialIds'

type OptimizerStockLevelsCardMaterialDetailHeaderProps = {
  isCollapsed: boolean
}

export const OptimizerStockLevelsCardMaterialDetailHeader: React.FC<
  OptimizerStockLevelsCardMaterialDetailHeaderProps
> = ({isCollapsed}) => {
  const {t} = useTranslation()
  const {spacing, palette} = useTheme()
  const {startOfPlan, endOfPlan} = usePlanningChartStartEnd()
  const {selectedInTableScheduleId} = useSelectedOptimizerPlan()
  const {data: storagesWithMaterials} = useHistoryStoragesWithMaterialQuery({
    timeFrame: [startOfPlan, endOfPlan]
  })

  const optimizedMaterialIds = useGetOptimizedMaterialIds()

  const isOptimizedPlanSelected = selectedInTableScheduleId !== undefined

  const isLoading = optimizedMaterialIds === undefined || storagesWithMaterials === undefined

  const {materialId, selectMaterialId} = useSelectedMaterialId()

  // filter only loaded material ids
  const filteredMaterialId =
    materialId && optimizedMaterialIds?.includes(materialId) ? materialId : undefined

  const unselectMaterial = useCallback(() => {
    selectMaterialId(undefined)
  }, [selectMaterialId])

  if (isLoading) {
    return <Skeleton height={toPixel(100)} variant="rectangular" />
  }

  return (
    <Stack direction="row" spacing={2} justifyContent="flex-end" alignItems="center">
      {!isCollapsed && (
        <Legend testId="silo_levels_legend">
          <LegendItem
            label={t('optimizer.selectedPlan')}
            indicator={{
              style: {backgroundColor: palette.primary.light},
              variant: 'circle'
            }}
          />
          {isOptimizedPlanSelected && (
            <LegendItem
              label={t('optimizer.currentPlan')}
              indicator={{
                style: {backgroundColor: customThemeConstants().palette.accent1},
                variant: 'circle'
              }}
            />
          )}
          <LegendItem label={t('optimizer.minLevel')}>
            <svg width={spacing(4)} height="3px">
              <line
                x1={0}
                x2={30}
                strokeWidth={1}
                y1={1}
                y2={1}
                stroke={palette.error.light}
                strokeDasharray="8,5"
              />
            </svg>
          </LegendItem>
        </Legend>
      )}
      <SelectMaterialIdDropdown
        selectedMaterialId={filteredMaterialId}
        changeSelectedMaterialId={selectMaterialId}
        materialIds={optimizedMaterialIds}
      />
      <div>
        <IconButton
          onClick={unselectMaterial}
          color="primary"
          {...dataTestId('close_material_detail_button')}
        >
          <Close />
        </IconButton>
      </div>
    </Stack>
  )
}
